import React, { useState, useEffect } from 'react';
import {
  Container, Grid, Typography, TextField, Button, CircularProgress, Box, Paper
} from '@mui/material';
import { makeStyles } from '@material-ui/core/styles';
import GaugeDesignComponent from './GaugeDesignComponent';
import axios from 'axios';
import ClosingTheGapAssessment from './ClosingGap';
import DynamicReport from '../DynamicPage/dynamicReport';
import { Autocomplete, Chip, IconButton } from '@mui/material';
import {
  GetApp as GetAppIcon,
  Print as PrintIcon  // Make sure this is imported
} from '@material-ui/icons'
import { ReportServices } from '../../../../Services/ReportServices';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

const reportServices = new ReportServices();

const useStyles = makeStyles((theme) => ({
  rechartBox: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
    maxWidth: 350,
    height: 300,
    margin: 'auto',
    [theme.breakpoints.down('md')]: {
      minHeight: 220,
      height: 220,
    },
    [theme.breakpoints.down('sm')]: {
      minHeight: 220,
      height: 220,
    },
    [theme.breakpoints.down('xs')]: {
      minHeight: 200,
      height: 200,
    },
  },
  bookmark: {
    position: 'relative',
    writingMode: 'vertical-rl',
    transform: 'rotate(180deg)',
    backgroundColor: '#1976d2',
    color: '#fff',
    borderRadius: '0 8px 8px 0',
    padding: '8px 4px',
    fontWeight: 600,
    textAlign: 'center',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '40px',
    '&::after': {
      content: '""',
      position: 'absolute',
      bottom: 0,
      left: 0,
      width: 0,
      height: 0,
      borderStyle: 'solid',
      borderWidth: '10px 10px 0 0',
      borderColor: '#1565c0 transparent transparent transparent',
    },
    [theme.breakpoints.down('xs')]: {
      width: '100%',
      minHeight: 32,
      writingMode: 'horizontal-tb',
      transform: 'none',
      padding: '6px 8px',
      borderRadius: '8px 8px 0 0',
      '&::after': {
        display: 'none',
      },
    },
  },
  // Enhanced styles for proper button alignment (40px to match size="small" inputs)
  buttonContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: '12px',
    height: '40px',
    width: '100%',
    [theme.breakpoints.down('xs')]: {
      flexWrap: 'wrap',
      minHeight: 40,
      justifyContent: 'flex-start',
    },
  },
  filterSection: {
    marginBottom: 32,
    padding: '20px 0',
  },
  actionButton: {
    height: '40px',
    minWidth: '80px',
  },
  iconButton: {
    height: '40px',
    width: '40px',
    padding: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: '1px solid #e0e0e0',
    borderRadius: '4px',
    cursor: 'pointer',
    backgroundColor: 'transparent',
    '&:hover': {
      backgroundColor: 'rgba(25, 118, 210, 0.04)',
      borderColor: '#1976d2',
    },
    '&:disabled': {
      cursor: 'not-allowed',
      opacity: 0.6,
    }
  },
  printContainer: {
    '& .no-print': {
      '@media print': {
        display: 'none !important'
      }
    }
  },
  // Add styles for PDF content
  gaugeContainer: {
    backgroundColor: '#ffffff',
    padding: '16px',
    '& svg': {
      backgroundColor: '#ffffff'
    },
    [theme.breakpoints.down('xs')]: {
      padding: '8px',
    },
  },
  filterGrid: {
    [theme.breakpoints.down('xs')]: {
      '& > .MuiGrid-item': {
        minWidth: 0,
      },
    },
  },
  insightsContainer: {
    width: '100%',
    maxWidth: '100%',
    overflowX: 'hidden',
    paddingLeft: 16,
    paddingRight: 16,
    [theme.breakpoints.down('xs')]: {
      paddingLeft: 8,
      paddingRight: 8,
    },
  },
  gaugeSection: {
    [theme.breakpoints.down('xs')]: {
      marginTop: 16,
    },
  },
  gaugeRow: {
    marginBottom: 32,
    backgroundColor: '#ffffff',
    padding: 16,
    minHeight: 200,
    [theme.breakpoints.down('sm')]: {
      marginBottom: 24,
      padding: 12,
      minHeight: 180,
    },
    [theme.breakpoints.down('xs')]: {
      marginBottom: 20,
      padding: 8,
      minHeight: 160,
    },
  },
  gaugeRowLabel: {
    fontSize: 20,
    [theme.breakpoints.down('xs')]: {
      fontSize: 16,
    },
  },
  closingGapSection: {
    backgroundColor: '#ffffff',
    padding: 16,
    marginTop: 32,
    [theme.breakpoints.down('xs')]: {
      padding: 8,
      marginTop: 20,
    },
  },
  rightColumnContent: {
    backgroundColor: '#ffffff',
    padding: 16,
    [theme.breakpoints.down('xs')]: {
      padding: 8,
    },
  },
}));

const groupTitlesMap = {
  G1: 'Organizational Positioning',
  G2: 'Organizational Development',
  G3: 'Talent Readiness and Mobility',
  G4: 'Continuous Learning',
  G5: 'Transformative Environment',
  G6: 'Intelligent Harmony'
};

const groupKeys = ['G1', 'G2', 'G3', 'G4', 'G5', 'G6'];

const GaugeDashboard = () => {
  const classes = useStyles();
  const [filters, setFilters] = useState({ customer: '', project: '' });
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [downloadReportLoading, setDownloadReportLoading] = useState(false);
  const [printReportLoading, setPrintReportLoading] = useState(false);

  const [customerOptions, setCustomerOptions] = useState([]);
  const [projectOptions, setProjectOptions] = useState([]);

  const fetchCustomers = async (project = '') => {
    try {
      const params = {};
      if (project) params.project = project;
      const res = await reportServices.getProjectReportDataMaturityModel('/main/user-survey-averages', { params });
      if (res.data.status && Array.isArray(res.data.data.filter.customer)) {
        setCustomerOptions(res.data.data.filter.customer || []);
      }
    } catch (error) {
      setCustomerOptions([]);
      console.error('Customer API Error', error);
    }
  };

  const fetchProjects = async (customer = '') => {
    try {
      const params = {};
      if (customer) params.company_name = customer;
      const res = await reportServices.getProjectReportDataMaturityModel('/main/user-survey-averages', { params });
      if (res.data.status && Array.isArray(res.data.data.filter.project)) {
        setProjectOptions(res.data.data.filter.project || []);
      }
    } catch (error) {
      setProjectOptions([]);
      console.error('Project API Error', error);
    }
  };

  const fetchSurveyAveragesWith = async (customFilters) => {
    setLoading(true);
    try {
      const params = {};
      if (customFilters.customer) params.company_name = customFilters.customer;
      if (customFilters.project) params.project = customFilters.project;
      const res = await reportServices.getProjectReportDataMaturityModel('/main/company-survey-averages', { params });
      if (res.data.status && Array.isArray(res.data.data) && res.data.data.length > 0) {
        setData(res.data.data);
      } else {
        setData([]);
      }
    } catch (error) {
      setData([]);
      console.error('API Error', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSurveyAverages = async (overrideFilters) => {
    const useFilters = overrideFilters || filters;
    await fetchSurveyAveragesWith(useFilters);
  };

  useEffect(() => {
    fetchCustomers();
    fetchProjects();
    fetchSurveyAverages();
    // eslint-disable-next-line
  }, []);

  const displayData = groupKeys.map(group => {
    const found = data.find(d => d.group === group);
    return found || { group, sq1_average: 0, sq2_average: 0 };
  });

  const closingGapData = displayData.map(groupData => ({
    name: groupTitlesMap[groupData.group] || groupData.group,
    current: typeof groupData.sq1_average === 'number' ? Math.round(groupData.sq1_average * 10) / 10 : 0,
    future: typeof groupData.sq2_average === 'number' ? Math.round(groupData.sq2_average * 10) / 10 : 0,
  }));

  // CSV export handler
  const handleExportCSV = async () => {
    setDownloadReportLoading(true);
    try {
      const header = ['Group', 'Current (SQ1)', 'Future (SQ2)'];
      const rows = displayData.map(groupData => [
        groupTitlesMap[groupData.group] || groupData.group,
        typeof groupData.sq1_average === 'number' ? Math.round(groupData.sq1_average * 10) / 10 : 0,
        typeof groupData.sq2_average === 'number' ? Math.round(groupData.sq2_average * 10) / 10 : 0,
      ]);
      const csvContent = [header, ...rows]
        .map(row => row.map(val => `"${val}"`).join(','))
        .join('\r\n');
      const blob = new Blob([csvContent], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'digital_maturity_insights.csv';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } finally {
      setDownloadReportLoading(false);
    }
  };

  // Enhanced Print/PDF Export Handler - Each group on separate page (Digital Talent)
  const handlePrintToPDF = async () => {
    setPrintReportLoading(true);

    try {
      console.log('Starting Digital Talent Maturity PDF generation with separate pages...');

      // Wait for all components to fully render
      await new Promise(resolve => setTimeout(resolve, 2000));

      // Create PDF with better margins
      const pdf = new jsPDF('landscape', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const margin = 8;

      // Hide filter section globally
      const filterElements = document.querySelectorAll('.no-print');
      filterElements.forEach(el => {
        el.style.display = 'none';
      });

      let pageNumber = 1;

      // Function to add header to each page
      const addHeader = (pdf, pageNum, isFirstPage = false) => {
        if (isFirstPage) {
          pdf.setFontSize(18);
          pdf.setFont(undefined, 'bold');
          pdf.text('Digital Talent Maturity Insights Report', pdfWidth / 2, 15, { align: 'center' });

          pdf.setFontSize(9);
          pdf.setFont(undefined, 'normal');

          let filterText = 'Applied Filters: ';
          if (filters.customer) filterText += `Customer: ${filters.customer}`;
          if (filters.project) filterText += `${filters.customer ? ', ' : ''}Project: ${filters.project}`;
          if (!filters.customer && !filters.project) filterText += 'No filters applied';

          pdf.text(filterText, margin, 25);

          const currentDate = new Date().toLocaleString();
          pdf.text(`Generated on: ${currentDate}`, pdfWidth - margin, 25, { align: 'right' });

          return 30;
        } else {
          pdf.setFontSize(14);
          pdf.setFont(undefined, 'bold');
          pdf.text('Digital Talent Maturity Insights Report (Continued)', pdfWidth / 2, 15, { align: 'center' });
          return 20;
        }
      };

      let yPosition = addHeader(pdf, pageNumber, true);

      // Process each group individually - CORRECTED SELECTOR
      for (let i = 0; i < displayData.length; i++) {
        const groupData = displayData[i];
        const groupTitle = groupTitlesMap[groupData.group] || groupData.group;

        console.log(`Processing group ${i + 1}: ${groupTitle}`);

        // FIXED: Use the correct selector for Digital Talent dashboard
        const gaugeRows = document.querySelectorAll('[data-testid="digital-gauge-row"]');
        const currentGaugeRow = gaugeRows[i];

        if (!currentGaugeRow) {
          console.warn(`Digital gauge row ${i} not found`);
          continue;
        }

        if (i > 0) {
          pdf.addPage();
          pageNumber++;
          yPosition = addHeader(pdf, pageNumber, false);
        }

        pdf.setFontSize(16);
        pdf.setFont(undefined, 'bold');
        pdf.text(`${groupTitle}`, margin, yPosition + 15);
        yPosition += 25;

        if (i === 0) {
          pdf.setFontSize(12);
          pdf.setFont(undefined, 'normal');

          yPosition += 15;
        }

        try {
          // Create a clean container with only the gauge columns
          const tempContainer = document.createElement('div');
          tempContainer.style.cssText = `
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 40px;
          padding: 20px;
          background-color: #ffffff;
          width: 800px;
          margin: 0 auto;
        `;

          // Find the gauge columns (skip the first column which is the bookmark)
          const gridItems = currentGaugeRow.querySelectorAll('.MuiGrid-item');
          const gaugeColumns = Array.from(gridItems).filter(item => {
            // Skip bookmark column and headers, only get gauge containers
            const hasGauge = item.querySelector('svg') || item.querySelector('canvas') ||
              item.querySelector('[class*="rechartBox"]') ||
              item.querySelector('.rechartBox');
            return hasGauge;
          });

          console.log(`Found ${gaugeColumns.length} gauge columns for group ${i + 1}`);

          gaugeColumns.forEach((column, index) => {
            const clonedColumn = column.cloneNode(true);

            // Clean up any bookmark elements in the cloned content
            const bookmarkElements = clonedColumn.querySelectorAll('.bookmark, [class*="bookmark"]');
            bookmarkElements.forEach(bookmark => bookmark.remove());

            // Ensure proper styling for gauges
            clonedColumn.style.cssText = `
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            padding: 10px;
          `;

            tempContainer.appendChild(clonedColumn);
          });

          // Only proceed if we found gauge columns
          if (tempContainer.children.length > 0) {
            // Temporarily add to DOM for capture
            document.body.appendChild(tempContainer);

            await new Promise(resolve => setTimeout(resolve, 500));

            const canvas = await html2canvas(tempContainer, {
              scale: 2,
              useCORS: true,
              allowTaint: true,
              backgroundColor: '#ffffff',
              logging: false,
              width: 800,
              height: 400,
              onclone: function (clonedDoc) {
                const clonedElements = clonedDoc.querySelectorAll('*');
                clonedElements.forEach(el => {
                  el.style.visibility = 'visible';
                  el.style.opacity = '1';

                  // Remove any transformations
                  if (el.style.transform) el.style.transform = '';
                  if (el.style.writingMode) el.style.writingMode = 'horizontal-tb';

                  if (el.tagName.toLowerCase() === 'svg' || el.tagName.toLowerCase() === 'canvas') {
                    el.style.display = 'block';
                  }
                });
              }
            });

            // Remove temporary container
            document.body.removeChild(tempContainer);

            if (canvas && canvas.width > 0 && canvas.height > 0) {
              const imgData = canvas.toDataURL('image/png', 1.0);

              const availableWidth = pdfWidth - (margin * 2);
              const availableHeight = pdfHeight - yPosition - 20;

              const aspectRatio = canvas.width / canvas.height;
              let imgWidth = availableWidth;
              let imgHeight = imgWidth / aspectRatio;

              if (imgHeight > availableHeight) {
                imgHeight = availableHeight;
                imgWidth = imgHeight * aspectRatio;
              }

              const xOffset = (pdfWidth - imgWidth) / 2;
              pdf.addImage(imgData, 'PNG', xOffset, yPosition, imgWidth, imgHeight);

              console.log(`Successfully added digital talent group ${i + 1} to PDF`);
            } else {
              console.warn(`Canvas is empty for group ${i + 1}`);
              pdf.setFontSize(12);
              pdf.text(`Error: Could not capture content for ${groupTitle}`, margin, yPosition + 30);
            }
          } else {
            console.warn(`No gauge columns found for group ${i + 1}`);
            pdf.setFontSize(12);
            pdf.text(`Error: No gauge data found for ${groupTitle}`, margin, yPosition + 30);
          }

        } catch (error) {
          console.error(`Error processing digital talent group ${i + 1}:`, error);
          pdf.setFontSize(12);
          pdf.text(`Error: Could not capture content for ${groupTitle}`, margin, yPosition + 30);
        }
      }

      // Add Closing The Gap Analysis on a separate page - CORRECTED SELECTOR
      const dynamicReportContainer = document.querySelector('#digital-closing-gap-section');
      if (dynamicReportContainer) {
        pdf.addPage();
        pageNumber++;
        yPosition = addHeader(pdf, pageNumber, false);

        pdf.setFontSize(16);
        pdf.setFont(undefined, 'bold');
        pdf.text('Closing The Gap Analysis', margin, yPosition + 15);
        yPosition += 25;

        try {
          const reportCanvas = await html2canvas(dynamicReportContainer, {
            scale: 2,
            useCORS: true,
            allowTaint: true,
            backgroundColor: '#ffffff',
            logging: false
          });

          if (reportCanvas && reportCanvas.width > 0 && reportCanvas.height > 0) {
            const reportImgData = reportCanvas.toDataURL('image/png', 1.0);

            const availableWidth = pdfWidth - (margin * 2);
            const availableHeight = pdfHeight - yPosition - 20;

            const aspectRatio = reportCanvas.width / reportCanvas.height;
            let imgWidth = availableWidth;
            let imgHeight = imgWidth / aspectRatio;

            if (imgHeight > availableHeight) {
              imgHeight = availableHeight;
              imgWidth = imgHeight * aspectRatio;
            }

            const xOffset = (pdfWidth - imgWidth) / 2;
            pdf.addImage(reportImgData, 'PNG', xOffset, yPosition, imgWidth, imgHeight);
          }
        } catch (error) {
          console.error('Error capturing digital talent closing gap report:', error);
        }
      }

      // Add page numbers
      const totalPages = pdf.internal.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        pdf.setPage(i);
        pdf.setFontSize(8);
        pdf.text(`Page ${i} of ${totalPages}`, pdfWidth / 2, pdfHeight - 5, { align: 'center' });
      }

      // Restore filter visibility
      filterElements.forEach(el => {
        el.style.display = '';
      });

      // Save PDF
      const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
      const filename = `Digital_Talent_Maturity_Dashboard_${timestamp}.pdf`;

      pdf.save(filename);
      console.log('Digital Talent Maturity PDF with separate pages generated successfully:', filename);

    } catch (error) {
      console.error('Digital Talent Maturity PDF generation failed:', error);
      alert('Failed to generate PDF. Please ensure all content is loaded and try again.');
    } finally {
      setPrintReportLoading(false);
    }
  };





  return (
    <Container className={`${classes.printContainer} ${classes.insightsContainer}`} maxWidth={false} disableGutters>
      <div id="digital-dashboard-container" style={{ backgroundColor: '#ffffff', minHeight: '100vh', width: '100%' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h4" sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>Insights Report</Typography>
        </Box>

        <Grid container spacing={3} className={`no-print ${classes.filterGrid} ${classes.filterSection}`}>
          <Grid item xs={12} sm={6} md={4}>
            <Autocomplete
              options={customerOptions}
              getOptionLabel={(option) => option.name || option.label || option.value || option}
              value={customerOptions.find(c => (c.id || c.value || c) === filters.customer) || null}
              onChange={async (e, newValue) => {
                const customerId = newValue?.id || newValue?.value || newValue || '';
                const updated = { customer: customerId, project: '' };
                setFilters(updated);
                await fetchProjects(customerId);
                await fetchSurveyAveragesWith(updated);
              }}
              isOptionEqualToValue={(option, value) =>
                (option.id || option.value || option) === (value.id || value.value || value)
              }
              renderOption={(props, option, { selected }) => (
                <li {...props}>
                  {option.name || option.label || option}
                </li>
              )}
              renderTags={(value, getTagProps) =>
                value ? (
                  <Chip
                    {...getTagProps({ index: 0 })}
                    label={value.name || value.label || value}
                    onDelete={async () => {
                      setFilters(prev => {
                        const updated = { ...prev, customer: '' };
                        setTimeout(async () => {
                          await fetchProjects('');
                          await fetchCustomers('');
                          await fetchSurveyAveragesWith(updated);
                        }, 0);
                        return updated;
                      });
                    }}
                  />
                ) : null
              }
              clearIcon={<span>&times;</span>}
              renderInput={(params) => <TextField {...params} label="Customer" size="small" fullWidth />}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <Autocomplete
              options={projectOptions}
              getOptionLabel={(option) => option.name || option.label || option.value || option}
              value={projectOptions.find(p => (p.id || p.value || p) === filters.project) || null}
              onChange={async (e, newValue) => {
                const projectId = newValue?.id || newValue?.value || newValue || '';
                setFilters(prev => {
                  const updated = { ...prev, project: projectId };
                  setTimeout(async () => {
                    await fetchCustomers(projectId);
                    await fetchSurveyAveragesWith(updated);
                  }, 0);
                  return updated;
                });
              }}
              isOptionEqualToValue={(option, value) =>
                (option.id || option.value || option) === (value.id || value.value || value)
              }
              renderOption={(props, option, { selected }) => (
                <li {...props}>
                  {option.name || option.label || option}
                </li>
              )}
              renderTags={(value, getTagProps) =>
                value ? (
                  <Chip
                    {...getTagProps({ index: 0 })}
                    label={value.name || value.label || value}
                    onDelete={async () => {
                      setFilters(prev => {
                        const updated = { ...prev, project: '' };
                        setTimeout(async () => {
                          await fetchCustomers('');
                          await fetchProjects('');
                          await fetchSurveyAveragesWith(updated);
                        }, 0);
                        return updated;
                      });
                    }}
                  />
                ) : null
              }
              clearIcon={<span>&times;</span>}
              renderInput={(params) => <TextField {...params} label="Project" size="small" fullWidth />}
            />
          </Grid>

          {/* Fixed Button Container with Both Export Options */}
          <Grid item xs={12} sm={12} md={4} style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-end' }}>
            <Box className={classes.buttonContainer}>
              <Button
                variant="contained"
                color="primary"
                onClick={fetchSurveyAverages}
                disabled={loading}
                className={classes.actionButton}
              >
                {loading ? <CircularProgress size={24} color="inherit" /> : 'Filter'}
              </Button>

              {/* CSV Export Button */}
              <button
                className={classes.iconButton}
                onClick={handleExportCSV}
                disabled={downloadReportLoading}
                title="Export as CSV"
                style={{
                  cursor: downloadReportLoading ? 'not-allowed' : 'pointer',
                  opacity: downloadReportLoading ? 0.6 : 1,
                }}
              >
                {downloadReportLoading ? (
                  <CircularProgress size={20} color="primary" />
                ) : (
                  <GetAppIcon color="primary" />
                )}
              </button>

              {/* PDF Print Button */}
              <button
                className={classes.iconButton}
                onClick={handlePrintToPDF}
                disabled={printReportLoading}
                title="Export as PDF"
                style={{
                  cursor: printReportLoading ? 'not-allowed' : 'pointer',
                  opacity: printReportLoading ? 0.6 : 1,
                }}
              >
                {printReportLoading ? (
                  <CircularProgress size={20} color="primary" />
                ) : (
                  <PrintIcon color="primary" />
                )}
              </button>
            </Box>
          </Grid>
        </Grid>

        <Box sx={{ mt: { xs: 2, sm: 4 } }} id="digital-gauge-dashboard-section" className={`${classes.gaugeContainer} ${classes.gaugeSection}`}>
          <Grid container spacing={{ xs: 2, sm: 3, md: 4 }}>
            {/* Left column: Gauges - balanced space for gauges and table */}
            <Grid item xs={12} md={8} lg={9}>
              {displayData.map((groupData, index) => (
                <Grid
                  container
                  spacing={2}
                  key={groupData.group}
                  alignItems="center"
                  className={classes.gaugeRow}
                  data-testid="digital-gauge-row"
                  style={{ visibility: 'visible', opacity: 1 }}
                >
                  <Grid item xs={12} sm={1}>
                    <Paper elevation={3} className={classes.bookmark}>
                      {groupTitlesMap[groupData.group] || ""}
                    </Paper>
                  </Grid>

                  <Grid item xs={12} sm={5}>
                    {index === 0 && (
                      <Typography align="center" className={classes.gaugeRowLabel} sx={{ mb: 2 }}>
                        Status Quo (Current)
                      </Typography>
                    )}
                    <Box
                      className={classes.rechartBox}
                      style={{ backgroundColor: '#ffffff' }}
                    >
                      <GaugeDesignComponent
                        label="SQ1"
                        score={
                          typeof groupData.sq1_average === 'number'
                            ? Math.round(groupData.sq1_average * 10) / 10
                            : 0
                        }
                      />
                    </Box>
                  </Grid>

                  <Grid item xs={12} sm={5}>
                    {index === 0 && (
                      <Typography align="center" className={classes.gaugeRowLabel} sx={{ mb: 2 }}>
                        Aspiration (Future)
                      </Typography>
                    )}
                    <Box
                      className={classes.rechartBox}
                      style={{ backgroundColor: '#ffffff' }}
                    >
                      <GaugeDesignComponent
                        label="SQ2"
                        score={
                          typeof groupData.sq2_average === 'number'
                            ? Math.round(groupData.sq2_average * 10) / 10
                            : 0
                        }
                      />
                    </Box>
                  </Grid>
                </Grid>
              ))}
            </Grid>

            {/* Right column: Dynamic Report */}
            <Grid item xs={12} md={4} lg={3}>
              <div className={classes.rightColumnContent}>
                <DynamicReport />
              </div>
            </Grid>
          </Grid>
        </Box>

        <Box id="digital-closing-gap-section" className={classes.closingGapSection}>
          <ClosingTheGapAssessment
            title="Closing The Gap"
            description="Digital Talent Maturity Insights report provides a comprehensive overview of your organization's digital talent maturity, highlighting key areas for improvement and growth."
            data={closingGapData}
          />
        </Box>
      </div>
    </Container>
  );
};

export default GaugeDashboard;
